import React, {useState} from 'react';
import {
  Button,
  Flex,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {IntentSource} from '../../../shared/intent';
import {TrashCan} from '@carbon/icons-react';
import {ObjectId} from 'bson';
import {DeleteIntentSourceModal} from './DeleteIntentSourceModal';

const columnHelper = createColumnHelper<IntentSource>();

const useColumns = (onDelete: (id: ObjectId) => void) => {
  return [
    columnHelper.accessor('label', {
      header: 'Label',
      id: 'label',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('id', {
      header: 'Source ID',
      id: 'sourceId',
      cell: (info) => <Text>{info.getValue().toString()}</Text>,
    }),
    columnHelper.accessor('id', {
      header: '',
      id: 'delete',
      cell: (info) => {
        const id = info.getValue();
        return (
          <Button
            aria-label="Delete intent source"
            fontWeight="500"
            fontSize="14"
            colorScheme="red"
            variant="outline"
            onClick={() => onDelete(id)}
          >
            <TrashCan />
          </Button>
        );
      },
    }),
  ];
};

export const IntentSourcesInfo = ({sources}: {sources: IntentSource[]}) => {
  const [sourceIdToDelete, setSourceIdToDelete] = useState<ObjectId>();

  const columns = useColumns((id) => setSourceIdToDelete(id));

  const table = useReactTable({
    data: sources,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!sources.length) {
    return (
      <Flex direction="column" align="center" p={8}>
        <Text textColor="kgray.300">No intent sources configured.</Text>
      </Flex>
    );
  }

  return (
    <>
      <TableContainer>
        <Table variant="simple">
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th key={header.id} isNumeric={header.column.id === 'delete'}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <Tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <Td key={cell.id} isNumeric={cell.column.id === 'delete'}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
      <DeleteIntentSourceModal
        isOpen={!!sourceIdToDelete}
        onClose={() => setSourceIdToDelete(undefined)}
        sourceIdToDelete={sourceIdToDelete}
      />
    </>
  );
};
